import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { LabelInput } from '../../../components/Input/Input';
import { alertError, alertSuccess } from '../../../components/Toast';
import InstallmentTable from '../../../screens/FinancialDashboard/components/InstallmentTable';
import {
  INSTALLMENT_STATUS,
  OS_CATEGORY_LABELS,
  STATUS,
} from '../../../core/constants/financial';
import { loadAccountPlans } from '../../../core/utils/accountPlan';
import { loadBanks } from '../../../core/utils/bank';
import { saveOSCharge } from '../../../core/utils/financialEntry';
import { prorateInstallments } from '../../../core/utils/installment';
import { todayISO } from '../../../core/utils/financialPeriod';
import { formatCurrency, parseMoneyValue } from '../../../core/utils/format';

// Modal de cobrança da OS. Mostra a composição por categoria (categoria → plano
// de contas → valor líquido) de forma transparente e gera UM único lançamento
// financeiro com as parcelas reais do cronograma; a separação por serviço vai
// para o breakdown (FinancialEntryBreakdown), lido apenas pela DRE.
const ChargeRateioModal = ({ isOpen, onClose, onSaved, prefilled }) => {
  const company = useSelector((state) => state.app.company || {});
  const user = useSelector((state) => state.app.user || {});

  const [accountPlans, setAccountPlans] = useState([]);
  const [banks, setBanks] = useState([]);
  const [issueDate, setIssueDate] = useState(todayISO());
  const [installmentCount, setInstallmentCount] = useState(1);
  const [installments, setInstallments] = useState([]);
  const [saving, setSaving] = useState(false);

  const breakdown = prefilled?.breakdown || [];

  // Carrega plano de contas e bancos ao abrir.
  useEffect(() => {
    if (!isOpen || !company.id) return;
    let cancelled = false;
    Promise.all([loadAccountPlans(company.id), loadBanks(company.id)]).then(
      ([plans, bankList]) => {
        if (cancelled) return;
        setAccountPlans(plans);
        setBanks(bankList);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [isOpen, company.id]);

  // Reseta o estado de parcelas/data sempre que (re)abre.
  useEffect(() => {
    if (!isOpen) return;
    setIssueDate(todayISO());
    setInstallmentCount(1);
    setInstallments([]);
  }, [isOpen, prefilled]);

  // Resolve cada categoria do breakdown ao seu plano de contas (por código).
  // categories: [{ key, code, gross, discount, value, account_plan_id, accountName, missing }]
  const categories = useMemo(() => {
    return breakdown.map((b) => {
      const plan = accountPlans.find(
        (a) => String(a.code) === String(b.code) && a.type === 'receita'
      );
      return {
        ...b,
        account_plan_id: plan?.id || null,
        accountName: plan?.name || null,
        missing: !plan,
      };
    });
  }, [breakdown, accountPlans]);

  const grandTotal = useMemo(
    () => categories.reduce((s, c) => s + (Number(c.value) || 0), 0),
    [categories]
  );

  const missingAccounts = categories.filter((c) => c.missing);

  const bankOptions = banks
    .filter((b) => b.status === STATUS.ATIVO)
    .map((b) => ({ value: b.id, label: b.name }));

  const generateInstallments = () => {
    if (grandTotal <= 0) {
      alertError('Não há valor a cobrar nesta OS.');
      return;
    }
    const count = Math.max(1, Math.min(48, Number(installmentCount) || 1));
    const schedule = prorateInstallments(grandTotal, count, issueDate);
    setInstallments(
      schedule.map((inst) => ({
        installment_number: inst.installment_number,
        due_date: inst.due_date,
        value: formatCurrency(inst.value),
        payment_method: '',
        bank_id: null,
        status: INSTALLMENT_STATUS.PENDENTE,
      }))
    );
  };

  // Gera 1 parcela automaticamente quando abre com valor e ainda sem parcelas.
  useEffect(() => {
    if (!isOpen) return;
    if (grandTotal > 0 && installments.length === 0) {
      const schedule = prorateInstallments(grandTotal, 1, issueDate);
      setInstallments(
        schedule.map((inst) => ({
          installment_number: inst.installment_number,
          due_date: inst.due_date,
          value: formatCurrency(inst.value),
          payment_method: '',
          bank_id: null,
          status: INSTALLMENT_STATUS.PENDENTE,
        }))
      );
    }
  }, [isOpen, grandTotal]);

  const handleSave = async () => {
    if (saving) return;

    if (grandTotal <= 0) {
      alertError('Não há valor a cobrar nesta OS.');
      return;
    }
    if (missingAccounts.length > 0) {
      const codes = missingAccounts.map((c) => c.code).join(', ');
      alertError(
        `Plano de contas não encontrado para: ${codes}. Cadastre essas contas em Plano de Contas antes de cobrar.`
      );
      return;
    }
    if (installments.length === 0) {
      alertError('Gere as parcelas antes de salvar.');
      return;
    }
    // RN-004: banco e forma de pagamento obrigatórios em todas as parcelas.
    const missingPayment = installments.find(
      (i) => !i.bank_id || !i.payment_method
    );
    if (missingPayment) {
      alertError(
        `Parcela ${missingPayment.installment_number} precisa de banco e forma de pagamento.`
      );
      return;
    }

    setSaving(true);
    try {
      const numericInstallments = installments.map((inst) => ({
        installment_number: inst.installment_number,
        due_date: inst.due_date,
        value: parseMoneyValue(inst.value),
        payment_method: inst.payment_method || null,
        bank_id: inst.bank_id || null,
        status: inst.status || INSTALLMENT_STATUS.PENDENTE,
      }));

      const { error } = await saveOSCharge({
        shared: {
          company_id: company.id,
          order_service_id: prefilled?.order_service_id || null,
          document: prefilled?.document || null,
          supplier_client: prefilled?.supplier_client || '',
          description: prefilled?.description || null,
          issue_date: issueDate,
          created_by: user.id,
        },
        categories: categories.map((c) => ({
          key: c.key,
          code: c.code,
          account_plan_id: c.account_plan_id,
          value: Number(c.value) || 0,
        })),
        installments: numericInstallments,
      });

      if (error) {
        alertError('Erro ao gerar a cobrança da OS.');
        return;
      }

      alertSuccess('Cobrança gerada!');
      if (onSaved) onSaved();
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fd-modal-overlay" onClick={onClose}>
      <div
        className="fd-modal fd-modal--scroll"
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER */}
        <div className="fd-modal-header">
          <h3 className="fd-modal-title">Cobrar Cliente — Rateio por Categoria</h3>
          <button
            className="fd-btn-close"
            type="button"
            onClick={onClose}
            style={{ background: '#ED2D2D', color: '#fff', borderColor: '#ED2D2D' }}
          >
            X CANCELAR
          </button>
        </div>

        {/* BODY */}
        <div className="fd-form-body">
          {/* CLIENTE / DOCUMENTO / DATA */}
          <div className="fd-form-row">
            <LabelInput label="Cliente" value={prefilled?.supplier_client || ''} disabled />
            <LabelInput label="Documento" value={prefilled?.document || ''} disabled />
            <LabelInput
              label="Data Emissão"
              type="date"
              value={issueDate}
              onChange={(value) => setIssueDate(value)}
            />
          </div>

          {/* TABELA DE RATEIO (RN06) */}
          <div className="fd-entry-installments-header" style={{ marginTop: 8 }}>
            <span className="fd-entry-installments-title">
              Composição da Receita (classificação automática)
            </span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="fd-modal-table" style={{ fontSize: 11 }}>
              <thead>
                <tr>
                  <th>Categoria</th>
                  <th>Plano de Contas</th>
                  <th style={{ textAlign: 'right' }}>Bruto</th>
                  <th style={{ textAlign: 'right' }}>Desconto</th>
                  <th style={{ textAlign: 'right' }}>Líquido</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((c) => (
                  <tr key={c.key}>
                    <td>{OS_CATEGORY_LABELS[c.key] || c.key}</td>
                    <td style={{ color: c.missing ? '#f16063' : undefined }}>
                      {c.missing
                        ? `${c.code} — conta não cadastrada`
                        : `${c.code} - ${c.accountName}`}
                    </td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(c.gross)}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(c.discount)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>
                      {formatCurrency(c.value)}
                    </td>
                  </tr>
                ))}
                {categories.length === 0 && (
                  <tr>
                    <td colSpan={5} className="fd-table-empty">
                      Nenhuma categoria com valor nesta OS.
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={4} style={{ textAlign: 'right', fontWeight: 700 }}>
                    Total Receita Bruta
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: '#70E000' }}>
                    {formatCurrency(grandTotal)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {missingAccounts.length > 0 && (
            <div
              style={{
                background: 'rgba(241,96,99,0.10)',
                border: '1px solid rgba(241,96,99,0.35)',
                color: '#f16063',
                padding: '8px 12px',
                borderRadius: 4,
                fontSize: 12,
                margin: '8px 0',
              }}
            >
              Alguns planos de contas não foram encontrados (
              {missingAccounts.map((c) => c.code).join(', ')}). Cadastre-os em
              Plano de Contas para gerar a cobrança classificada.
            </div>
          )}

          {/* PARCELAS */}
          <div className="fd-entry-installments-header">
            <span className="fd-entry-installments-title">Parcelas</span>
            <div className="fd-entry-installments-controls">
              <span style={{ fontSize: 12, color: '#ccc' }}>Qtd:</span>
              <input
                type="number"
                className="fd-installment-input"
                style={{ width: 55, textAlign: 'center' }}
                value={installmentCount}
                min={1}
                max={48}
                onChange={(e) => setInstallmentCount(e.target.value)}
                onBlur={(e) => {
                  const num = Number(e.target.value) || 1;
                  setInstallmentCount(Math.max(1, Math.min(48, num)));
                }}
              />
              <button
                className="fd-btn-green"
                type="button"
                onClick={generateInstallments}
                style={{ padding: '5px 12px', fontSize: 11 }}
              >
                GERAR
              </button>
            </div>
          </div>

          <InstallmentTable
            installments={installments}
            bankOptions={bankOptions}
            onUpdate={setInstallments}
          />
        </div>

        {/* FOOTER */}
        <div className="fd-modal-footer">
          <button
            className="fd-btn-close"
            type="button"
            onClick={onClose}
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            className="fd-btn-green"
            type="button"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'GERANDO...' : 'GERAR COBRANÇA'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChargeRateioModal;
